import React, { FC, useState, useEffect, useContext } from "react";
import { useQuery } from "@apollo/client";
import { Button, Space, Tabs } from "antd";
import { useDebouncedCallback } from "use-debounce";
import {
  MatterEditorView,
  WorkplanExportButton,
  PricingLevel,
  ShareMatterButton,
  PricingSidebar,
  MatterClientAndMatterDetails,
  Matter,
  ClientValue,
  pricingFieldsByType,
  pricingFieldsByTypeFlattened,
  MatterExternalMatterUpdate,
  PricingType,
  PricingTarget,
  showAutoClosingAlert,
  TeamMembershipIds,
  TeamMembership,
  Phase,
  createTotals,
  Work,
  Spinner,
} from "@design-system";
import {
  ORGANISATIONAL_UNITS_QUERY,
  FILTERED_CLIENTS_QUERY,
  FIND_MATTER,
  FIND_ACCRUED_FEES_FOR_USER,
  BEDROCK_SUGGEST_ASSUMPTIONS_QUERY,
  BEDROCK_SUGGEST_EXCLUSIONS_QUERY,
  NOTIFICATIONS_QUERY,
  FEE_EARNER_LEVELS_QUERY,
} from "../graphql/query";
import {
  UPDATE_MATTER,
  CREATE_CLIENT,
  UPDATE_PHASE,
  ASSIGN_WORKS_TO_PHASE,
  DELETE_WORK,
  UPDATE_MATTER_TEAM,
} from "../graphql/mutation";
import { ExternalMatterStatus, MatterStatus } from "../graphql/types";
import { WORK_FRAGMENT } from "../graphql/fragment/work-fragment";
import { ScopeAndAdjustmentsTabContainer } from "./scope-and-adjustments/scope-and-adjustments-tab-container";
import { WorkplanPreviewContainer } from "./workplan-preview-container";
import { ShareMatterContainer } from "./share-matter-container";
import { FeeEarnerCascaderContainer } from "./fee-earner-cascader-container";
import { TeamBuilderContainer } from "./team-builder/team-builder-container";
import { FeeRiskProfileTabContainer } from "./fee-risk-profile-tab-container";
import { TopDownPricingContainer } from "./top-down-pricing";
import { TechnologyTabContainer } from "./technology-tab-container";
import { TimelineTabContainer } from "./timeline/timeline-tab-container";
import { HistoricalMattersContainer } from "./historical-matters";

import {
  useActiveTabState,
  SCOPE_TAB,
  FEE_RISK_PROFILE_TAB,
  CLIENT_MATTER_DETAILS,
  TEAM_TAB,
  VPDTab,
  PROPOSAL_PREVIEW_TAB,
  TOP_DOWN_PRICING_TAB,
  TECHNOLOGY_TAB,
  HISTORICAL_MATTERS_TAB,
  TIMELINE_TAB,
} from "../hooks/use-active-tab";
import styles from "./matter-editor-container.module.css";
import { useExternalMattersQueryLazy } from "../hooks/external-matters";
import { useMutation, useSettings } from "../hooks";
import { useFilteredClientsLazy } from "../hooks/clients";
import { doesMatterTaxStatusExcludeTax } from "../helpers";
import { useFindMatterQuery } from "../hooks/matters";
import { priceOptionsToPrismaUpdate, setSingleOrRangeProps } from "../helpers/price-option";
import produce from "immer";
import { HeartFilled, HeartOutlined } from "@ant-design/icons";
import { PriceOptions, TaxStatus, FindOrganisationalUnitsQuery } from "~/gql/graphql";
import { TOGGLE_FAVOURITE } from "~/graphql/mutation/toggle-favourite";
import { ProposalPreviewPage } from "./proposal-preview/proposal-preview-page";
import { GlobalContext } from "~/context/global-context";
import { useQueryClient } from "@tanstack/react-query";

const { "tab-pane": tabPaneClassname, "pricing-summary-footer": pricingSummaryFooterClassName } =
  styles as any;

const matterHasTitle = (matter: Pick<Matter, "title">) => {
  return matter.title !== "";
};

const matterHasPricingStructure = (matter: Pick<Matter, "pricingType" | "pricingLevel">) => {
  return matter.pricingType !== PricingType.None && matter.pricingLevel !== PricingLevel.None;
};

const editorUnlocked = (
  matter: Pick<Matter, "pricingType" | "pricingLevel" | "title"> | null
): boolean => {
  return !!matter && matterHasTitle(matter) && matterHasPricingStructure(matter);
};

const startTab = (isEditorUnlocked: boolean, pricingType: PricingType): VPDTab => {
  return isEditorUnlocked
    ? pricingType === PricingType.TopDown
      ? TOP_DOWN_PRICING_TAB
      : TEAM_TAB
    : CLIENT_MATTER_DETAILS;
};

export const MatterEditorContainer: FC<{ matterId: number }> = ({ matterId }) => {
  const [showShareMatter, setShowShareMatter] = useState(false);
  const [showWorkplanPreview, setShowWorkplanPreview] = useState(false);
  const { loading: findMatterLoading, error, data } = useFindMatterQuery({ id: matterId });
  const [assignedTeamMembers, setAssignedTeamMembers] = useState<TeamMembershipIds[]>([]);
  const [refetchingSuggestions, setRefetchingSuggestions] = useState(false);
  const [topDownRefreshTrigger, setTopDownRefreshTrigger] = useState(0);
  const [timelineActivationKey, setTimelineActivationKey] = useState(0);

  const currencySymbol = data?.findMatter?.currency.symbol || "";

  const matter = data?.findMatter!;

  // Get QueryClient from the context
  const queryClient = useQueryClient();

  const currentUserInfo = useContext(GlobalContext);
  const useAi = currentUserInfo.enableAi;
  const useTechPricing =
    currentUserInfo.enableTechPricing ||
    (matter?.technologyCharges && matter?.technologyCharges.length > 0);

  const { data: feeEarnerLevelsData = undefined } = useQuery(FEE_EARNER_LEVELS_QUERY);

  // the following two useEffects run to add any rates that are assigned to the matter to the team, otherwise they will not show up in the scope editor.
  useEffect(() => {
    if (matter && matter.pricingType !== PricingType.TopDown) {
      const uniqueAssignedTeamMembers = matter.phases?.reduce((acc: TeamMembershipIds[], phase) => {
        const phaseTeamMembers =
          phase.work
            ?.filter((w) => w.selectedRate !== null)
            .map((work) => ({
              rateId: work.selectedRate!.id,
              feeEarnerId: work.feeEarner?.id ?? null,
            })) || [];

        phaseTeamMembers.forEach((member) => {
          const isDuplicate = acc.find(
            (existingMember: TeamMembershipIds) =>
              existingMember.rateId === member.rateId &&
              existingMember.feeEarnerId === member.feeEarnerId
          );

          if (!isDuplicate) {
            acc.push(member);
          }
        });

        return acc;
      }, []);

      setAssignedTeamMembers(uniqueAssignedTeamMembers || []);
    }
  }, [matter]);

  // if any assigned rates are not in the team, add them
  useEffect(() => {
    const teamMembershipIdsToAdd = assignedTeamMembers
      .filter(
        (toAdd) =>
          !matter.teamMemberships?.some(
            (tm) =>
              tm?.rate?.id === toAdd?.rateId &&
              (tm?.feeEarner?.id === toAdd?.feeEarnerId ||
                (tm?.feeEarner === null && toAdd?.feeEarnerId === null))
          )
      )
      .filter(Boolean);

    if (teamMembershipIdsToAdd.length > 0) {
      updateMatterTeam({
        variables: {
          matterId: matterId,
          teamMembersToAdd: teamMembershipIdsToAdd,
          teamMembersToRemove: [],
        },
      });
    }
  }, [assignedTeamMembers]);

  if (!matter && !findMatterLoading) {
    window.location.href = "/error/404";
    return null;
  }

  const clientFilterData = useFilteredClientsLazy(10, true);

  const { data: { organisationalUnits: practiceAreas = [] } = {} } =
    useQuery<FindOrganisationalUnitsQuery>(ORGANISATIONAL_UNITS_QUERY);

  const [updateMatter, { loading: updateMatterLoading }] = useMutation(UPDATE_MATTER, {
    refetchQueries: [{ query: FIND_MATTER, variables: { id: matterId } }],
  });
  const [updatePhase] = useMutation(UPDATE_PHASE);
  const [toggleFavourite] = useMutation(TOGGLE_FAVOURITE);

  const [selectedClient, setSelectedClient] = useState<ClientValue | undefined | null>(undefined);

  type PriceOptionFields =
    | "fixedChosen"
    | "cappedChosen"
    | "feeRiskLower"
    | "feeRiskUpper"
    | "topDownFeeTarget"
    | "topDownHoursTarget"
    | "topDownRateDiscount";
  const handlePriceOptionChange = (field: PriceOptionFields, value: number | null) => {
    updateMatter({
      variables: {
        id: matter.id,
        data: {
          priceOptions: {
            update: {
              data: {
                [field]: { set: value },
              },
            },
          },
        },
      },
      optimisticResponse: {
        updateMatter: {
          ...matter,
          priceOptions: {
            ...matter.priceOptions!,
            [field]: value,
          },
        },
      },
    });
  };

  const dBhandlePriceOptionChange = useDebouncedCallback(
    // function
    (field: PriceOptionFields, value: number | null) => {
      handlePriceOptionChange(field, value);
    },
    // delay in ms
    600
  );

  const [createClient] = useMutation(CREATE_CLIENT, {
    update: async (cache, mutationResult) => {
      const newClient = mutationResult.data?.createClient;
      const data: any = cache.readQuery({
        query: FILTERED_CLIENTS_QUERY,
        variables: clientFilterData?.queryVars,
      });

      cache.writeQuery({
        query: FILTERED_CLIENTS_QUERY,
        variables: clientFilterData?.queryVars
          ? {
              ...clientFilterData.queryVars,
              filter: { search: newClient?.name },
            }
          : {
              limit: 10,
              offset: 0,
              filter: {
                name: [newClient?.name],
                externalReference: newClient?.externalReference,
              },
            },
        data: {
          filteredClients: {
            clients: [...(data?.filteredClients?.clients || []), newClient],
            total: (data?.filteredClients?.total || []) + 1,
          },
        },
      });
      if (newClient?.name) {
        await updateMatter({
          variables: {
            id: matterId,
            data: {
              client: { connect: { id: newClient.id } },
            },
          },
        });
        setSelectedClient({ ...newClient, type: "Client" } as ClientValue);
        clientFilterData.search(newClient?.name);
      }
    },
  });

  const [assignWorksToPhase, { loading: assignWorksLoading }] = useMutation(ASSIGN_WORKS_TO_PHASE, {
    update: (cache, curr) => {
      const newWorks = curr.data?.assignWorksToPhase;

      newWorks?.forEach((target) => {
        const newWorkRef = cache.writeFragment({
          data: target,
          fragment: WORK_FRAGMENT,
        });

        cache.modify({
          id: `Phase:${target?.phaseId}`,
          fields: {
            work(existingWorkRefs = [], { readField }: any) {
              if (existingWorkRefs.some((ref: any) => readField("id", ref) === target?.id)) {
                return existingWorkRefs;
              }
              return [...existingWorkRefs, newWorkRef];
            },
          },
        });
      });
    },
  });
  const [deleteWork] = useMutation(DELETE_WORK);
  const createWorkCacheRemover =
    (phaseId: number | null) =>
    (cache: any, { data: { deleteWork } }: any) => {
      if (phaseId) {
        cache.modify({
          id: `Phase:${phaseId}`,
          fields: {
            work(existingWorkRefs = [], { readField }: any) {
              return existingWorkRefs.filter(
                (workRef: any) => deleteWork.workId !== readField("id", workRef)
              );
            },
          },
        });
      } else {
        cache.modify({
          id: `Matter:${matterId}`,
          fields: {
            work(existingWorkRefs = [], { readField }: any) {
              return existingWorkRefs.filter(
                (workRef: any) => deleteWork.workId !== readField("id", workRef)
              );
            },
          },
        });
      }
    };

  const [updateMatterTeam] = useMutation(UPDATE_MATTER_TEAM, {
    update(cache, { data }) {
      const updateTeamMembers = data?.updateTeamMembers;
      if (updateTeamMembers) {
        cache.updateQuery({ query: FIND_MATTER, variables: { id: matterId } }, (currentData) => {
          if (currentData?.findMatter) {
            return produce(currentData, (draft) => {
              draft.findMatter.teamMemberships = updateTeamMembers.teamMemberships;
              draft.findMatter.phases = updateTeamMembers.phases;
            });
          }
        });
      }
    },
  });

  const {
    data: suggestedAssumptionsData,
    loading: suggestedAssumptionsLoading,
    refetch: refetchSuggestedAssumptions,
  } = useQuery(BEDROCK_SUGGEST_ASSUMPTIONS_QUERY, {
    variables: {
      matterId: matter?.id,
    },
    skip: !matter?.id || !useAi,
  });

  const {
    data: suggestedExclusionsData,
    loading: suggestedExclusionsLoading,
    refetch: refetchSuggestedExclusions,
  } = useQuery(BEDROCK_SUGGEST_EXCLUSIONS_QUERY, {
    variables: {
      matterId: matter?.id,
    },
    skip: !matter?.id || !useAi,
  });

  const handleRefetchSuggestions = async () => {
    setRefetchingSuggestions(true);
    await Promise.all([refetchSuggestedAssumptions(), refetchSuggestedExclusions()]);
    setRefetchingSuggestions(false);
  };

  const { settings, loading: settingsLoading } = useSettings();

  const countryCode = matter?.officeLocation?.countryCode || settings?.defaultCountryCode;

  const isEditorUnlocked = editorUnlocked(data?.findMatter || null);
  const tab = startTab(isEditorUnlocked, matter?.pricingType || PricingType.None);

  const externalMatterData = useExternalMattersQueryLazy();

  const [activeTab, setActiveTab] = useActiveTabState(tab);

  useEffect(() => {
    if (activeTab === TIMELINE_TAB) {
      setTimelineActivationKey((prevKey) => prevKey + 1);
    }
  }, [activeTab]);

  if (findMatterLoading || settingsLoading) return <Spinner show />;
  if (error) return <div>{error.message}</div>;

  const matterExcludesTax = doesMatterTaxStatusExcludeTax(matter?.taxStatus);

  const defaultTeamBuilderMessage = `Team members have been assigned to all phases of your proposal. Manage team members in specific phases on the Scope tab!`;

  // moved from pricing-sidebar.tsx
  let pricingOptions = matter.priceOptions ? { ...matter.priceOptions } : ({} as PriceOptions);
  if (
    matter.pricingLevel === PricingLevel.FeeEarner &&
    matter.pricingType === PricingType.EstimatedRange
  ) {
    const totalWorkCost = { lower: 0, upper: 0 };
    for (const p of matter.phases || []) {
      for (const w of p.work || []) {
        totalWorkCost.lower += w.price?.lower || 0;
        totalWorkCost.upper += w.price?.upper || 0;
      }
    }

    pricingOptions.estimatedRangeSuggestedLower = totalWorkCost.lower
      ? totalWorkCost.lower
      : pricingOptions?.estimatedRangeSuggestedLower;

    pricingOptions.estimatedRangeSuggestedUpper = totalWorkCost.upper
      ? totalWorkCost.upper
      : pricingOptions?.estimatedRangeSuggestedUpper;
  } else if (
    matter.pricingLevel === PricingLevel.Phase &&
    matter.pricingType === PricingType.EstimatedRange
  ) {
    const totalPhaseCosts = { lower: 0, upper: 0 };
    for (const p of matter.phases || []) {
      if (p.priceOptions) {
        const pricingTranforms = pricingFieldsByType(PricingType.EstimatedRange, p.priceOptions);
        totalPhaseCosts.lower += (pricingTranforms?.chosen as { lower: number }).lower || 0;
        totalPhaseCosts.upper += (pricingTranforms?.chosen as { upper: number }).upper || 0;
      }
    }

    pricingOptions.estimatedRangeSuggestedLower = totalPhaseCosts.lower
      ? totalPhaseCosts.lower
      : pricingOptions?.estimatedRangeSuggestedLower;

    pricingOptions.estimatedRangeSuggestedUpper = totalPhaseCosts.upper
      ? totalPhaseCosts.upper
      : pricingOptions?.estimatedRangeSuggestedUpper;
  } else if (
    matter.pricingLevel === PricingLevel.Phase &&
    matter.pricingType !== PricingType.None
  ) {
    let totalPhaseCosts = 0;
    for (const p of matter.phases || []) {
      if (p.priceOptions) {
        const pricingTranforms = pricingFieldsByType(matter.pricingType, p.priceOptions);
        totalPhaseCosts += (pricingTranforms?.suggested as number) || 0;
      }
    }
    pricingOptions = {
      ...pricingOptions,
      ...(matter.pricingType === PricingType.Fixed && { fixedFeeSuggested: totalPhaseCosts }),
      ...(matter.pricingType === PricingType.Capped && { cappedSuggested: totalPhaseCosts }),
      ...(matter.pricingType === PricingType.Estimated && { estimatedSuggested: totalPhaseCosts }),
    };
  }

  const uplifts = matter?.uplifts ? matter!.uplifts : [];
  const discounts = matter?.discounts ? matter!.discounts : [];

  const sortedPhases = [...(matter.phases || [])]
    .sort((a, b) => a.order - b.order)
    .map((phase) => ({
      ...phase,
      tasks: [...(phase.tasks || [])].sort((a, b) => a.order - b.order),
    }));

  const { subTotalExcludingVat, legalFeesBase, legalFeesWithPartial } = createTotals(
    pricingFieldsByType(matter.pricingType, matter.priceOptions!),
    matter.phases,
    matter.disbursements,
    uplifts,
    discounts,
    matter.work,
    matter.priceOptions?.topDownRateDiscount ?? 0,
    matter.pricingLevel,
    matter.pricingType,
    (matter.officeLocation?.taxLevelPercent || settings?.taxLevelPercent) ?? 0,
    matter.waiveAdminCharge ? 0 : settings.administrationCharge
  );

  if (!data?.findMatter) {
    return null;
  }

  const handleRemoveTopDownFeeEarnerLevel = async (teamMembershipId: number, workIds: number[]) => {
    updateMatterTeam({
      variables: {
        matterId: matterId,
        teamMembersToAdd: [],
        teamMembersToRemove: [
          {
            rateId: teamMembershipId,
          },
        ],
      },
    });
    if (workIds && workIds.length > 0) {
      for (const workId of workIds) {
        await deleteWork({
          variables: { workId },
          update: createWorkCacheRemover(null),
          optimisticResponse: { deleteWork: { workId, __typename: "DeleteWorkResult" } },
        });
      }
    }
  };

  const handleAddTopDownFeeEarnerLevel = async (feeEarnerLevelId: number, rateId: number) => {
    const updateMatterTeamResult = await updateMatterTeam({
      variables: {
        matterId: matterId,
        teamMembersToAdd: [
          {
            rateId: rateId,
          },
        ],
        teamMembersToRemove: [],
      },
    });
    const updateMatterResult = await updateMatter({
      variables: {
        id: matterId,
        data: {
          work: {
            create: [
              {
                feeEarnerLevel: { connect: { id: feeEarnerLevelId } },
                selectedRate: { connect: { id: rateId } },
                timeSingle: 0,
              },
            ],
          },
        },
      },
    });
    const work = updateMatterResult.data?.updateMatter.work;
    const newWork = work?.find((w) => w.feeEarnerLevel?.id === feeEarnerLevelId);
    const teamMemberships = updateMatterTeamResult.data?.updateTeamMembers.teamMemberships;
    const newTeamMembership = teamMemberships?.find((t) => t.rateId === rateId);

    // Trigger refresh to ensure props are updated with the new data
    setTopDownRefreshTrigger((prev) => prev + 1);

    return { teamMembershipId: newTeamMembership?.id, work: newWork as Work };
  };

  const handleAddTopDownFeeEarner = async (
    feeEarnerId: number | null,
    feeEarnerLevelId: number | null,
    rateId: number
  ) => {
    const updateMatterTeamResult = await updateMatterTeam({
      variables: {
        matterId: matterId,
        teamMembersToAdd: [
          {
            rateId: rateId,
            feeEarnerId: feeEarnerId,
          },
        ],
        teamMembersToRemove: [],
      },
    });
    const updateMatterResult = await updateMatter({
      variables: {
        id: matterId,
        data: {
          work: {
            create: [
              {
                ...(feeEarnerLevelId && {
                  feeEarnerLevel: { connect: { id: feeEarnerLevelId } },
                }),
                ...(feeEarnerId && {
                  feeEarner: { connect: { id: feeEarnerId } },
                }),
                selectedRate: { connect: { id: rateId } },
                timeSingle: 0,
              },
            ],
          },
        },
      },
    });
    const work = updateMatterResult.data?.updateMatter.work;
    const newWork = work?.find(
      (w) =>
        (feeEarnerId && w.feeEarner?.id === feeEarnerId) ||
        (feeEarnerLevelId && w.feeEarnerLevel?.id === feeEarnerLevelId)
    );
    const teamMemberships = updateMatterTeamResult.data?.updateTeamMembers.teamMemberships;
    const newTeamMembership = teamMemberships?.find((t) => t.rateId === rateId);

    // Trigger refresh to ensure props are updated with the new data
    setTopDownRefreshTrigger((prev) => prev + 1);

    return { teamMembershipId: newTeamMembership?.id, work: newWork as Work };
  };

  const tabs: any[] = [
    <Tabs.TabPane tab="Client &amp; Matter Details" key="clientmatterdetails">
      <MatterClientAndMatterDetails
        updateMatterLoading={updateMatterLoading}
        client={selectedClient}
        setCurrentClient={setSelectedClient}
        template={matter?.template ? "template" : "matter"}
        matter={matter as any}
        pmsName={settings?.organisationPmsName || ""}
        practiceAreas={practiceAreas}
        externalMatterData={externalMatterData}
        onClientSave={(clientValue) => createClient({ variables: { data: clientValue as any } })}
        onMatterUpdate={async (matterData) => {
          const {
            id,
            practiceAreaId: organisationalUnitId,
            officeLocationId,
            ...updateData
          } = matterData;
          await updateMatter({
            refetchQueries: [
              {
                query: FIND_ACCRUED_FEES_FOR_USER,
                variables: {
                  filter: {
                    matterStatus: MatterStatus.Live,
                  },
                },
              },
            ],
            onCompleted: () => {
              return showAutoClosingAlert({
                message: "Matter Updated",
                type: "success",
              });
            },
            onError: () => {
              return showAutoClosingAlert({
                message: "Matter Update Failed",
                type: "error",
              });
            },
            update: (cache, newData) => {
              if (newData.data?.updateMatter.externalMatterId) {
                cache.modify({
                  id: `ExternalMatter:${newData.data?.updateMatter.externalMatterId}`,
                  fields: {
                    status() {
                      return (
                        newData.data?.updateMatter.externalMatter?.status ??
                        ExternalMatterStatus.Unassigned
                      );
                    },
                    matter() {
                      return {
                        id: matter.id,
                      };
                    },
                  },
                });
              }
              // If the external matter id has changed, we need to update the old one
              if (
                matter.externalMatterId !== newData.data?.updateMatter.externalMatterId ||
                (matter.externalMatterId && !newData.data?.updateMatter.externalMatterId)
              ) {
                cache.modify({
                  id: `ExternalMatter:${matter.externalMatterId}`,
                  fields: {
                    status() {
                      return ExternalMatterStatus.Unassigned;
                    },
                    matter() {
                      return undefined;
                    },
                  },
                });
              }

              cache.evict({
                id: "ROOT_QUERY",
                fieldName: "filteredMatters",
              });
              cache.evict({
                id: "ROOT_QUERY",
                fieldName: "findAccruedFeesForUser",
              });
            },
            variables: {
              id,
              data: {
                ...(organisationalUnitId && {
                  organisationalUnit: { connect: { id: organisationalUnitId } },
                }),
                ...(updateData?.externalMatterId === null &&
                  matter.externalMatterId && {
                    externalMatter: { disconnect: { id: { equals: matter.externalMatterId } } },
                  }),
                ...(updateData.externalMatterId && {
                  externalMatter: { connect: { id: updateData.externalMatterId } },
                }),
                ...(updateData?.title && {
                  title: { set: updateData.title },
                }),
                ...(updateData.status && { status: { set: updateData.status } }),

                ...(updateData?.pricingType && {
                  pricingType: { set: updateData.pricingType },
                }),
                ...(updateData?.pricingLevel && {
                  pricingLevel: { set: updateData.pricingLevel },
                }),
                ...(updateData?.pricingTarget && {
                  pricingTarget: { set: updateData.pricingTarget },
                }),
                ...(updateData?.leadFeeEarnerId && {
                  feeEarner: {
                    connect: {
                      id: updateData.leadFeeEarnerId,
                    },
                  },
                }),
                ...(updateData?.clientId && {
                  client: {
                    connect: {
                      id: updateData.clientId,
                    },
                  },
                }),
                ...(updateData?.matterTaxExempt != null && {
                  taxStatus: {
                    set:
                      updateData.matterTaxExempt === true ? TaxStatus.MatterExempt : TaxStatus.None,
                  },
                }),
                ...(updateData?.matterCurrencyId && {
                  currency: {
                    connect: {
                      id: updateData.matterCurrencyId,
                    },
                  },
                }),
                ...(officeLocationId && {
                  officeLocation: {
                    connect: {
                      id: officeLocationId,
                    },
                  },
                }),
                ...(updateData?.confidential != null && {
                  confidential: { set: updateData.confidential },
                }),
              },
            },
          });
          queryClient.invalidateQueries(["proposal", matterId]);
          if (updateData?.title && updateData.title !== matter.title) {
            handleRefetchSuggestions();
          }
        }}
        onDelete={async () => {
          const id = matter.id;
          await updateMatter({
            refetchQueries: [
              {
                query: FIND_ACCRUED_FEES_FOR_USER,
                variables: {
                  filter: {
                    matterStatus: MatterStatus.Live,
                  },
                },
              },
              {
                query: NOTIFICATIONS_QUERY,
                variables: { type: "Alert" },
              },
            ],
            onCompleted: () => {
              return showAutoClosingAlert({
                message: "Matter Deleted",
                type: "success",
              });
            },
            onError: () => {
              return showAutoClosingAlert({
                message: "Matter Delete Failed",
                type: "error",
              });
            },
            update: (cache) => {
              cache.evict({ id: `Matter:${id}` });
              cache.gc();
            },
            variables: {
              id,
              data: {
                deletedAt: { set: new Date() },
                deletedBy: {
                  connect: { id: currentUserInfo?.currentUser?.userId },
                },
              },
            },
          });
        }}
        clientFilterData={clientFilterData}
        feeEarnersCascaderRender={() => (
          <FeeEarnerCascaderContainer
            size="small"
            showSearch
            defaultFeeEarner={matter.leadFeeEarner as any}
          />
        )}
      />
    </Tabs.TabPane>,
    <Tabs.TabPane tab="Historical Matters" key={HISTORICAL_MATTERS_TAB}>
      <HistoricalMattersContainer
        matterId={matterId}
        matterTitle={matter.title}
        clientName={matter.client?.name ?? ""}
        aiGeneratedMatterSummary={matter.aiGeneratedMatterSummary}
        orgUnitId={matter.organisationalUnit?.id}
        phases={matter.phases || []}
        setTopDownRefreshTrigger={setTopDownRefreshTrigger}
        currencyId={matter.currency?.id}
        pinnedMatterIds={matter.pinnedMatters?.map((matter) => matter.id) || []}
      />
    </Tabs.TabPane>,
    matter.pricingType === PricingType.TopDown ? (
      <Tabs.TabPane tab="Top Down Pricing" key={TOP_DOWN_PRICING_TAB}>
        <TopDownPricingContainer
          matterId={matterId}
          currency={data?.findMatter.currency}
          gpmThreshold={matter.gpmDangerThreshold}
          clientId={matter.client?.id}
          organisationalUnitId={matter?.organisationalUnit?.id}
          topDownRates={matter.teamMemberships || []}
          topDownWork={matter.work || []}
          pricingTarget={matter.pricingTarget ?? PricingTarget.Fee}
          topDownFeeTarget={matter.priceOptions?.topDownFeeTarget ?? 0}
          onTopDownFeeTargetChange={(value) => {
            dBhandlePriceOptionChange("topDownFeeTarget", value);
          }}
          topDownHoursTarget={matter.priceOptions?.topDownHoursTarget ?? 0}
          onTopDownHoursTargetChange={(value) => {
            dBhandlePriceOptionChange("topDownHoursTarget", value);
          }}
          topDownRateDiscount={matter.priceOptions?.topDownRateDiscount ?? 0}
          onTopDownRateDiscountChange={(value) => {
            dBhandlePriceOptionChange("topDownRateDiscount", value);
          }}
          onFeeEarnerLevelRateChange={async (oldRateId, newRateId) => {
            updateMatterTeam({
              variables: {
                matterId: matterId,
                teamMembersToAdd: [
                  {
                    rateId: newRateId,
                  },
                ],
                teamMembersToRemove: [
                  {
                    rateId: oldRateId,
                  },
                ],
              },
            });
          }}
          onRemoveFeeEarnerLevel={handleRemoveTopDownFeeEarnerLevel}
          onAddFeeEarnerLevel={handleAddTopDownFeeEarnerLevel}
          onAddFeeEarner={handleAddTopDownFeeEarner}
          feeEarnerLevels={feeEarnerLevelsData?.feeEarnerLevels.records || []}
          refreshTrigger={topDownRefreshTrigger}
        />
      </Tabs.TabPane>
    ) : (
      <Tabs.TabPane tab="Team" key={TEAM_TAB}>
        <TeamBuilderContainer
          currency={data?.findMatter.currency}
          matterId={matterId}
          clientId={matter.client?.id}
          organisationalUnitId={matter?.organisationalUnit?.id}
          externalMatterId={matter.externalMatter?.id}
          teamRates={matter.teamMemberships || []}
          phases={matter.phases || []}
          assignedRateIds={(
            matter.phases?.flatMap(
              (phase) => phase.work?.map((work) => work.selectedRate?.id) || []
            ) || []
          ).filter((id): id is number => id !== undefined)}
          onUpdateMatterTeam={async (data) => {
            updateMatterTeam({
              variables: {
                matterId: matterId,
                teamMembersToAdd: data.teamMembersToAdd,
                teamMembersToRemove: data.teamMembersToRemove,
              },
            });
          }}
          onAssignTeamToPhases={(
            teamMembers: TeamMembership[],
            phases: Phase[],
            message: string = defaultTeamBuilderMessage
          ) => {
            phases.forEach((phase) => {
              const teamMembersToAdd =
                teamMembers.filter(
                  (tm) =>
                    !phase.work?.some(
                      (work) =>
                        work.selectedRate?.id === tm?.rate?.id &&
                        (work.feeEarner?.id === tm?.feeEarner?.id ||
                          (work.feeEarner === null && tm?.rateId === null))
                    )
                ) || [];
              const dataToCreate = teamMembersToAdd.map((tm) => ({
                phase: { connect: { id: phase.id } },
                selectedRate: { connect: { id: tm.rate?.id } },
                feeEarner: tm.rate?.feeEarner?.id
                  ? { connect: { id: tm.rate?.feeEarner?.id } }
                  : tm.feeEarner
                  ? { connect: { id: tm.feeEarner.id } }
                  : undefined,
                feeEarnerLevel: tm.rate?.feeEarnerLevel?.id
                  ? { connect: { id: tm.rate?.feeEarnerLevel?.id } }
                  : undefined,
              }));
              assignWorksToPhase({
                variables: {
                  data: dataToCreate,
                },
                onCompleted: () => {
                  showAutoClosingAlert({
                    message: message,
                    type: "success",
                    duration: 10,
                  });
                },
              });
            });
          }}
          assignWorksLoading={assignWorksLoading}
          findMatterLoading={findMatterLoading}
          onDeleteTeamMember={(rateId, feeEarnerId) => {
            const worksToDelete = (
              matter.phases?.flatMap((phase) => {
                return phase.work
                  ?.filter(
                    (work) => work.selectedRate?.id === rateId && work.feeEarner?.id === feeEarnerId
                  )
                  .map((work) => ({ workId: work.id, phaseId: phase.id }));
              }) || []
            ).filter(
              (item): item is { workId: number; phaseId: number } =>
                item?.workId !== undefined && item?.phaseId !== undefined
            );

            worksToDelete.forEach(({ workId, phaseId }) =>
              deleteWork({
                variables: { workId },
                update: createWorkCacheRemover(phaseId),
                optimisticResponse: { deleteWork: { workId, __typename: "DeleteWorkResult" } },
              })
            );
          }}
          pricingLevel={matter.pricingLevel}
        />
      </Tabs.TabPane>
    ),
    <Tabs.TabPane tab="Scope & Adjustments" key={SCOPE_TAB}>
      <ScopeAndAdjustmentsTabContainer
        title={matter.title}
        clientName={matter.client?.name ?? ""}
        currencySymbol={currencySymbol}
        countryCode={countryCode ?? undefined}
        matterId={matterId}
        phases={matter.phases || []}
        pricingLevel={matter.pricingLevel}
        pricingType={matter.pricingType}
        organisationalUnitId={matter?.organisationalUnit?.id || 0}
        teamMembers={matter.teamMemberships || []}
        handleRefetchSuggestions={handleRefetchSuggestions}
        practiceAreas={practiceAreas}
        aiGeneratedMatterSummary={matter.aiGeneratedMatterSummary}
        suggestedAssumptions={
          (!refetchingSuggestions && suggestedAssumptionsData?.bedrockSuggestAssumptions) ||
          undefined
        }
        suggestedAssumptionsLoading={suggestedAssumptionsLoading || refetchingSuggestions}
        suggestedExclusions={
          (!refetchingSuggestions && suggestedExclusionsData?.bedrockSuggestExclusions) || undefined
        }
        suggestedExclusionsLoading={suggestedExclusionsLoading || refetchingSuggestions}
        disbursements={matter.disbursements || []}
        excludeTax={matterExcludesTax}
        taxLabel={matter.officeLocation?.taxLabel || settings.taxLabel}
        taxLevelPercent={(matter.officeLocation?.taxLevelPercent || settings?.taxLevelPercent) ?? 0}
        uplifts={matter.uplifts || []}
        discounts={matter.discounts || []}
        setTopDownRefreshTrigger={setTopDownRefreshTrigger}
      />
    </Tabs.TabPane>,
    <Tabs.TabPane tab="Timeline" key={TIMELINE_TAB}>
      <TimelineTabContainer
        matterId={matterId}
        phases={sortedPhases || []}
        matterStartDate={matter.startDate}
        matterDeadlineDate={matter.deadlineDate}
        currencySymbol={currencySymbol}
        pricingLevel={matter.pricingLevel}
        timelineActivationKey={timelineActivationKey}
        onUpdateMatterDates={(startDate, deadlineDate) => {
          updateMatter({
            variables: {
              id: matter.id,
              data: {
                startDate: { set: startDate },
                deadlineDate: { set: deadlineDate },
              },
            },
            optimisticResponse: {
              updateMatter: {
                ...matter,
                startDate,
                deadlineDate,
              },
            },
          });
        }}
      />
    </Tabs.TabPane>,
    useTechPricing && (
      <Tabs.TabPane tab="Technology" key={TECHNOLOGY_TAB} className={tabPaneClassname}>
        <TechnologyTabContainer
          matterId={matterId}
          technologyCharges={matter.technologyCharges || []}
          currency={data?.findMatter.currency}
        />
      </Tabs.TabPane>
    ),
  ];

  if (matter.pricingType === PricingType.EstimatedRange) {
    tabs.push(
      <Tabs.TabPane tab={"Fee Risk Profile"} key={FEE_RISK_PROFILE_TAB}>
        <FeeRiskProfileTabContainer
          matterId={matter.id}
          currencySymbol={currencySymbol}
          legalFees={legalFeesWithPartial}
          feeRiskLower={pricingOptions?.feeRiskLower ?? null}
          feeRiskUpper={pricingOptions?.feeRiskUpper ?? null}
          fixedFeeAlternative={pricingOptions?.fixedChosen ?? null}
          cappedFeeAlternative={pricingOptions?.cappedChosen ?? null}
          onFeeRiskLowerChange={(value) => {
            dBhandlePriceOptionChange("feeRiskLower", value);
          }}
          onFeeRiskUpperChange={(value) => {
            dBhandlePriceOptionChange("feeRiskUpper", value);
          }}
          onFixedFeeAlternativeChange={(value) => {
            dBhandlePriceOptionChange("fixedChosen", value);
          }}
          onCappedFeeAlternativeChange={(value) => {
            dBhandlePriceOptionChange("cappedChosen", value);
          }}
          showCappedFee={matter.feeRiskCappedFeeInclude ?? true}
        />
      </Tabs.TabPane>
    );
  }

  tabs.push(
    <Tabs.TabPane tab="Proposal Preview" key={PROPOSAL_PREVIEW_TAB}>
      <ProposalPreviewPage pricingType={matter.pricingType} matterId={matterId} />
    </Tabs.TabPane>
  );

  return (
    <>
      <MatterEditorView
        parentMatter={
          matter.parent && {
            id: matter.parent.id,
            title: matter.parent.title,
            matters: matter.parent.matters.map((m) => ({ id: m.id, title: m.title })),
          }
        }
        clientFilterData={clientFilterData}
        editorUnlocked={isEditorUnlocked}
        matter={matter as any}
        onClientSave={(clientValue) => createClient({ variables: { data: clientValue as any } })}
        onMatterUpdate={async (matterData) => {
          const { id, practiceAreaId: organisationalUnitId, ...updateData } = matterData;
          await updateMatter({
            variables: {
              id,
              data: {
                ...(updateData.leadFeeEarnerId && {
                  feeEarner: {
                    connect: { id: updateData.leadFeeEarnerId },
                  },
                }),
                ...(organisationalUnitId && {
                  organisationalUnit: { connect: { id: organisationalUnitId } },
                }),
                ...(updateData?.title && { title: { set: updateData.title } }),
                ...(updateData?.status && {
                  status: { set: updateData.status },
                }),
                ...(updateData?.pricingType && {
                  pricingType: { set: updateData.pricingType },
                }),
                ...(updateData?.pricingLevel && {
                  pricingLevel: { set: updateData.pricingLevel },
                }),
              },
            },
          });
        }}
        onMatterStatusUpdate={async (matterStatusData) => {
          await updateMatter({
            variables: {
              id: matterStatusData.id,
              data: { status: { set: matterStatusData.status } },
            },
          });
        }}
        activeTab={activeTab}
        onChangeTab={setActiveTab}
        settings={settings}
        tabs={tabs}
        sidebarContents={{
          pricing: (
            <PricingSidebar
              currencySymbol={currencySymbol}
              taxLabel={matter.officeLocation?.taxLabel || settings.taxLabel}
              pricingSummaries={matter.priceOptions!}
              gpmThreshold={matter.gpmDangerThreshold}
              taxLevelPercent={
                (matter.officeLocation?.taxLevelPercent || settings?.taxLevelPercent) ?? 0
              }
              excludeTax={matterExcludesTax}
              onFixedPriceAlternativeChange={(value) => {
                dBhandlePriceOptionChange("fixedChosen", value);
              }}
              fixedPriceAlternative={pricingOptions?.fixedChosen}
              subTotalExcludingVat={subTotalExcludingVat}
              legalFees={legalFeesBase}
              administrationCharge={settings.administrationCharge}
              onChosenPriceRangeChange={({ lower, upper }) => {
                updateMatter({
                  variables: {
                    id: matterId,
                    data: {
                      priceOptions: {
                        update: {
                          data: {
                            estimatedRangeChosenLower: { set: lower },
                            estimatedRangeChosenUpper: { set: upper },
                          },
                        },
                      },
                    },
                  },
                  optimisticResponse: {
                    updateMatter: {
                      ...matter,
                      priceOptions: {
                        ...matter,
                        __typename: "PriceOptions",
                        ...(matter.priceOptions && { ...matter.priceOptions }),
                        estimatedGpm: matter.priceOptions?.estimatedGpm ?? null,
                        estimatedSuggested: matter.priceOptions?.estimatedSuggested ?? null,
                        estimatedChosen: matter.priceOptions?.estimatedChosen ?? null,
                        estimatedRangeGpm: matter.priceOptions?.estimatedRangeGpm ?? null,
                        fixedGpm: matter.priceOptions?.fixedGpm ?? null,
                        fixedChosen: matter.priceOptions?.fixedChosen ?? null,
                        fixedSuggested: matter.priceOptions?.fixedSuggested ?? null,
                        cappedChosen: matter.priceOptions?.cappedChosen ?? null,
                        cappedSuggested: matter.priceOptions?.cappedSuggested ?? null,
                        cappedGpm: matter.priceOptions?.cappedGpm ?? null,
                        estimatedRangeSuggestedLower:
                          matter.priceOptions?.estimatedRangeSuggestedLower ?? null,
                        estimatedRangeSuggestedUpper:
                          matter.priceOptions?.estimatedRangeSuggestedUpper ?? null,
                        estimatedRangeChosenLower: lower
                          ? lower
                          : matter?.priceOptions?.estimatedRangeChosenLower
                          ? matter.priceOptions.estimatedRangeChosenLower
                          : 0,
                        estimatedRangeChosenUpper: upper
                          ? upper
                          : matter?.priceOptions?.estimatedRangeChosenUpper
                          ? matter.priceOptions.estimatedRangeChosenUpper
                          : 0,
                        feeRiskLower: matter.priceOptions?.feeRiskLower ?? null,
                        feeRiskUpper: matter.priceOptions?.feeRiskUpper ?? null,
                        topDownFeeTarget: matter.priceOptions?.topDownFeeTarget ?? null,
                        topDownHoursTarget: matter.priceOptions?.topDownHoursTarget ?? null,
                        topDownRateDiscount: matter.priceOptions?.topDownRateDiscount ?? null,
                      },
                    },
                  },
                });
              }}
              onChosenPriceChange={(value: number) => {
                const { pricingType } = matter;

                updateMatter({
                  variables: {
                    id: matterId,
                    data: {
                      priceOptions: {
                        update:
                          pricingType == PricingType.Fixed
                            ? {
                                data: {
                                  fixedChosen: { set: value },
                                },
                              }
                            : pricingType == PricingType.Capped
                            ? {
                                data: {
                                  cappedChosen: { set: value },
                                },
                              }
                            : {
                                data: {
                                  estimatedChosen: { set: value },
                                },
                              },
                      },
                    },
                  },
                  optimisticResponse: {
                    updateMatter: {
                      ...matter,
                      ...pricingFieldsByTypeFlattened(pricingType, matter.priceOptions!),
                      ...setSingleOrRangeProps(pricingType, "single", value, "chosen"),
                    },
                  },
                });
              }}
              matter={matter as any}
              isTemplate={false}
              onPhasePriceChange={(phaseId, priceDetails) => {
                if (phaseId) {
                  updatePhase({
                    variables: {
                      phaseId,
                      matterId,
                      data: {
                        priceOptions: {
                          update: {
                            data: priceOptionsToPrismaUpdate(priceDetails),
                          },
                        },
                      },
                    },
                  });
                }
              }}
              onWaiveAdminChargeChange={(value, reason) => {
                updateMatter({
                  variables: {
                    id: matterId,
                    data: {
                      waiveAdminCharge: { set: value },
                      ...(value && reason && { waiveAdminChargeReason: { set: reason } }),
                    },
                  },
                  optimisticResponse: {
                    updateMatter: {
                      ...matter,
                      waiveAdminCharge: value,
                      ...(value && reason && { waiveAdminChargeReason: reason }),
                    },
                  },
                });
              }}
              footer={
                <>
                  <Space align="center" size="small" className={pricingSummaryFooterClassName}>
                    <ShareMatterButton
                      onClick={() => setShowShareMatter(true)}
                      disabled={
                        !!matter.confidential &&
                        matter.createdBy?.id !== currentUserInfo?.currentUser?.userId
                      }
                    />
                    {/* <ProposalExportButton onClick={() => setShowProposalPreview(true)} /> */}
                    {matter.pricingLevel === PricingLevel.FeeEarner && (
                      <WorkplanExportButton onClick={() => setShowWorkplanPreview(true)} />
                    )}
                    <Button
                      icon={
                        !matter.isFavourite ? (
                          <HeartOutlined />
                        ) : (
                          <HeartFilled style={{ color: "red" }} />
                        )
                      }
                      type="primary"
                      size="small"
                      onClick={() =>
                        toggleFavourite({
                          variables: { id: matterId, favourite: !matter.isFavourite },
                          update: (cache, newData) => {
                            cache.updateQuery(
                              {
                                query: FIND_MATTER,
                                variables: { id: matterId },
                              },
                              (currentData) => {
                                if (!currentData?.findMatter) return;

                                return {
                                  findMatter: {
                                    ...currentData.findMatter,
                                    isFavourite: newData.data?.toggleFavourite.isFavourite ?? false,
                                  },
                                };
                              }
                            );
                          },
                          onCompleted: (newData) => {
                            showAutoClosingAlert({
                              message: `Matter ${
                                newData.toggleFavourite.isFavourite ? "added to" : "removed from"
                              } favourites`,
                              type: "success",
                            });
                          },
                        })
                      }
                    />
                  </Space>
                </>
              }
              useTechPricing={useTechPricing ?? false}
            />
          ),
          //   history: <VersionsHistoryContainer matterId={matterId} />,
        }}
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onMatterExternalMatterUpdate={function (matter: MatterExternalMatterUpdate): Promise<void> {
          throw new Error("Function not implemented.");
        }}
      />
      {showShareMatter && (
        <ShareMatterContainer
          matterId={matterId}
          matterTitle={matter?.title}
          externalMatterId={matter?.externalMatterId ?? undefined}
          clientName={matter?.client?.name}
          clientId={matter?.client?.id}
          onClose={() => setShowShareMatter(false)}
          opts={{ isParentMatter: false }}
        />
      )}
      {showWorkplanPreview && (
        <WorkplanPreviewContainer
          matterId={matterId}
          onClose={() => setShowWorkplanPreview(false)}
        />
      )}
    </>
  );
};
